import styled, { css } from 'styled-components'
import pluralize from 'pluralize'
import React, { UIEvent, useEffect, useRef, useState, WheelEvent } from 'react'
import KeywordsTable from './keywordsTable/KeywordsTable'
import useKeywordsTableData from '../../hooks/useKeywordsTableData'
import Hint from '../../../../shared/hint/Hint'
import { ReactComponent as CheckIcon } from '../../assets/Check.svg'
import HintTitle from './HintTitle'
import { useDatasetsContext } from '../../state/useDatasets'
import { useKeywordsStateContext } from '../../state/useKeywordsState'

const ContainerStyled = styled.div`
  position: relative;
  padding-bottom: 16px;
  outline: none;
`

const ScrollableContainerStyled = styled.div`
  overflow-x: auto;
  margin-left: 540px;
  border-left: 1px solid ${({ theme }) => theme.colors.grey.primary}
`

const TopScrollContainerStyled = styled.div<{ $isVisible: boolean }>`
  display: ${({ $isVisible }) => ($isVisible ? 'block' : 'none')};
  overflow-x: auto;
  overflow-y: hidden;
  height: 16px;
  margin-left: 540px;
  border-left: 1px solid ${({ theme }) => theme.colors.grey.primary};
`

const TopScrollContentStyled = styled.div<{ $width: number }>`
  width: ${({ $width }) => `${$width}px`};
  height: 1px;
`

const titleStyles = css`
  font-size: 18px;
  line-height: 22px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.black};
`

const TitleStyled = styled.span`
  ${titleStyles};
  position: sticky;
`

const TitleWrapperStyled = styled.div<{ $hasTopScroll: boolean }>`
    position: absolute;
    left: 20px;
    top: ${({ $hasTopScroll }) => ($hasTopScroll ? '16px' : '0')};
    display: flex;
    flex-direction: column;
    gap: 10px;
`

const DatasetsCountStyled = styled.span`
  ${titleStyles};

`

const TextWrapperStyled = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: 16px;
`

const HeaderStyled = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`

const DescriptionWrapperStyled = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 10px;
`

const DescriptionStyled = styled.p`
    font-size: 14px;
    font-weight: 400;
    line-height: 16px;
    color: ${({ theme }) => theme.colors.grey.dark};
`

const CheckIconStyled = styled(CheckIcon)`
    width: 16px;
    height: 16px;
    display: inline-block;
    vertical-align: middle;
`

const SummaryTextStyled = styled.p`
    font-size: 14px;
    font-weight: 400;
    line-height: 16px;
    color: ${({ theme }) => theme.colors.grey.dark};
`

const KeywordsSearchResults = () => {
  const datasetsCount = useKeywordsTableData().length
  const { isLoading } = useDatasetsContext()
  const { submittedKeywords } = useKeywordsStateContext()
  const containerRef = useRef<HTMLDivElement>(null)
  const topScrollRef = useRef<HTMLDivElement>(null)
  const tableScrollRef = useRef<HTMLDivElement>(null)
  const [scrollMetrics, setScrollMetrics] = useState({ width: 0, isVisible: false })

  useEffect(() => {
    const updateScrollMetrics = () => {
      const tableScroll = tableScrollRef.current

      setScrollMetrics({
        width: tableScroll?.scrollWidth ?? 0,
        isVisible: Boolean(tableScroll && tableScroll.scrollWidth > tableScroll.clientWidth)
      })
    }

    updateScrollMetrics()
    window.addEventListener('resize', updateScrollMetrics)

    return () => window.removeEventListener('resize', updateScrollMetrics)
  }, [datasetsCount, submittedKeywords])

  const handleTopScroll = (event: UIEvent<HTMLDivElement>) => {
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollLeft = event.currentTarget.scrollLeft
    }
  }

  const handleTableScroll = (event: UIEvent<HTMLDivElement>) => {
    if (topScrollRef.current) {
      topScrollRef.current.scrollLeft = event.currentTarget.scrollLeft
    }
  }

  const handleHorizontalWheel = (event: WheelEvent<HTMLDivElement>) => {
    const tableScroll = tableScrollRef.current

    if (!tableScroll || !containerRef.current?.contains(document.activeElement)) {
      return
    }

    const maxScrollLeft = tableScroll.scrollWidth - tableScroll.clientWidth
    const scrollDelta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY
    const nextScrollLeft = Math.max(0, Math.min(tableScroll.scrollLeft + scrollDelta, maxScrollLeft))

    if (nextScrollLeft === tableScroll.scrollLeft) {
      return
    }

    event.preventDefault()
    tableScroll.scrollLeft = nextScrollLeft

    if (topScrollRef.current) {
      topScrollRef.current.scrollLeft = nextScrollLeft
    }
  }

  const handleContainerClick = () => {
    containerRef.current?.focus({ preventScroll: true })
  }

  if (isLoading) {
    return <></>
  }
  return (
    <ContainerStyled
      ref={containerRef}
      tabIndex={0}
      onClick={handleContainerClick}
    >
      <TopScrollContainerStyled
        ref={topScrollRef}
        $isVisible={scrollMetrics.isVisible}
        onScroll={handleTopScroll}
        onWheel={handleHorizontalWheel}
      >
        <TopScrollContentStyled $width={scrollMetrics.width} />
      </TopScrollContainerStyled>
      <ScrollableContainerStyled ref={tableScrollRef} onScroll={handleTableScroll} onWheel={handleHorizontalWheel}>
        <TextWrapperStyled>
          <HeaderStyled>
            <TitleStyled>
              Availability of variables for the selected search term(s)
            </TitleStyled>
            <Hint withDefaultColor placement="top" maxWidth={280} title={<HintTitle />} />
          </HeaderStyled>
          <DescriptionWrapperStyled>
            <DescriptionStyled>
              Click on search term to compare across datasets.
            </DescriptionStyled>
            <DescriptionStyled>
              Click on
              {' '}
              <CheckIconStyled />
              {' '}
              to see variables associated with each search term for the selected dataset.
            </DescriptionStyled>
          </DescriptionWrapperStyled>
        </TextWrapperStyled>
        <TitleWrapperStyled $hasTopScroll={scrollMetrics.isVisible}>
          <DatasetsCountStyled>
            {`${pluralize('Dataset', datasetsCount, true)} found`}
          </DatasetsCountStyled>
          <SummaryTextStyled>
            Click on dataset link to see summary.
          </SummaryTextStyled>
        </TitleWrapperStyled>
        <KeywordsTable />
      </ScrollableContainerStyled>
    </ContainerStyled>
  )
}

export default KeywordsSearchResults
